/**
 * DayHeader - La cabecera del día.
 *
 * Antes eran tres bloques con marco: el resumen del día, una tarjeta con la fecha y el
 * tipo de día, y otra con tres desplegables siempre desplegados. Tres cajas para decir
 * cuatro cosas, y las tres compitiendo por la atención antes de llegar a las comidas,
 * que es a lo que se viene.
 *
 * Ahora es una sola zona sin marcos, separada por líneas finas:
 *   - la fecha y el tipo de día en una línea,
 *   - la configuración resumida en texto ("4 comidas · tras comida 2 · intra + post"),
 *     que se despliega solo cuando se quiere cambiar algo,
 *   - los tres macros del día en filas con su nombre completo,
 *   - el peri y el detalle por comida, en pequeño.
 */
import React from 'react';
import { Dumbbell, Moon, Check, UtensilsCrossed, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';
import ConfigSection from './ConfigSection';
import { leerMacro } from '../../lib/estadoDelMacro';
import { alDecima } from '../../lib/numeros';
import { totalDelDia, cifraDelDia } from '../../lib/estadoDelDia';
import { ahoraDeLaApp } from '../../lib/horaEspana';


/** «Hoy», «Mañana», «Ayer» o a cuántos días está, debajo de la fecha (maqueta del 15-10). */
const cuandoEs = (fecha) => {
    const hoy = new Date(`${ahoraDeLaApp().toLocaleDateString('en-CA')}T12:00:00`);
    const n = Math.round((new Date(`${fecha}T12:00:00`) - hoy) / 864e5);
    return n === 0 ? 'Hoy' : n === 1 ? 'Mañana' : n === -1 ? 'Ayer'
        : n === 2 ? 'Pasado mañana' : n === -2 ? 'Anteayer'
        : n > 1 ? `En ${n} días` : `Hace ${-n} días`;
};

const aFecha = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const diasEntre = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 864e5);
const INICIAL = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

/**
 * LA TIRA DE LA SEMANA (handoff de Nutrición, 16-10). Sustituye a las flechas: los siete días
 * de la semana del día que se ve, tocables, cada uno con un punto de estado. Tocar un día es
 * `changeDate(diferencia)`, lo mismo que pulsar las flechas esas veces; deslizar a los lados
 * salta una semana.
 *
 * El punto: verde, el día montado y cuadrado; ámbar, a medias o montado sin cuadrar; gris,
 * vacío. Los de los otros días salen de `GET /diets/semana` (lo mismo que pinta Mi semana);
 * el del día abierto, de lo que hay en pantalla, para que cambie al momento.
 */
const TiraSemanal = ({ currentDate, semana, estadoDelDia, onIrA }) => {
    const base = new Date(`${currentDate}T12:00:00`);
    const lunes = new Date(base);
    lunes.setDate(base.getDate() - ((base.getDay() + 6) % 7));
    const hoy = ahoraDeLaApp().toLocaleDateString('en-CA');
    const porFecha = Object.fromEntries((semana?.dias || []).map((d) => [d.fecha, d]));
    const punto = (fecha) => {
        if (fecha === currentDate) {
            if (estadoDelDia === 'listo') return 'bg-ok';
            if (estadoDelDia === 'sin_crear' || !estadoDelDia) return 'bg-white/20';
            return 'bg-amber-400';
        }
        const d = porFecha[fecha];
        if (!d || d.estado === 'sin_montar') return 'bg-white/20';
        return d.estado === 'montada' && d.is_cuadrado ? 'bg-ok' : 'bg-amber-400';
    };
    const toque = React.useRef(null);
    return (
        <div className="grid grid-cols-7 gap-1" data-testid="tira-semanal"
            onTouchStart={(e) => { toque.current = e.touches[0]?.clientX ?? null; }}
            onTouchEnd={(e) => {
                const x0 = toque.current;
                toque.current = null;
                const x1 = e.changedTouches[0]?.clientX;
                if (x0 == null || x1 == null || Math.abs(x1 - x0) < 60) return;
                onIrA(x1 < x0 ? 7 : -7);
            }}>
            {INICIAL.map((ini, i) => {
                const d = new Date(lunes);
                d.setDate(lunes.getDate() + i);
                const fecha = aFecha(d);
                const elegido = fecha === currentDate;
                return (
                    <button key={fecha} type="button" onClick={() => onIrA(diasEntre(currentDate, fecha))}
                        data-testid={`tira-dia-${fecha}`} aria-pressed={elegido}
                        aria-label={`${d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })}${fecha === hoy ? ', hoy' : ''}`}
                        className={`flex flex-col items-center gap-1 py-2 rounded-xl transition-colors ${
                            elegido ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted/60'
                        } ${fecha === hoy && !elegido ? 'ring-1 ring-inset ring-brand/50' : ''}`}>
                        <span className="text-[11px] font-semibold opacity-75">{ini}</span>
                        <span className="font-heading text-xl font-bold leading-none">{d.getDate()}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${elegido ? 'bg-background' : punto(fecha)}`} />
                    </button>
                );
            })}
        </div>
    );
};

const enMinuscula = (label) => label.toLowerCase();
const yJunto = (xs) => xs.length > 1 ? `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}` : xs[0];

/**
 * EL ESTADO DEL DÍA EN UNA PASTILLA (rediseño del 14-10). Sustituye a la banda de dos
 * líneas: se ve sin leer, y dice POR QUÉ el día no cuadra, que son dos cosas distintas y
 * se arreglan distinto:
 *   - comidas sin alimentos  -> hay que crearlas (el icono de los cubiertos);
 *   - alimentos que no llegan -> hay que subir algún macro (la flecha), y para eso está
 *     «Terminar», que reparte lo que falta entre las comidas.
 */
const EstadoDelDia = ({ estadoDelDia, sinCrear, periSinCrear = [], nombreDe = (k) => k, macros, onTerminarDia, terminando }) => {
    if (!estadoDelDia) return null;
    const pastilla = 'inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border';
    if (estadoDelDia === 'listo') {
        return (
            <span data-testid="dia-banda-listo" className={`${pastilla} border-emerald-500/40 bg-emerald-500/10 text-emerald-400`}>
                <Check className="w-3.5 h-3.5" /> Día cuadrado
            </span>
        );
    }
    if (estadoDelDia === 'sin_crear') {
        return (
            <span data-testid="dia-banda-sin_crear" className={`${pastilla} border-border bg-muted/40 text-muted-foreground`}>
                Día sin empezar
            </span>
        );
    }
    if (sinCrear.length > 0) {
        return (
            <span data-testid="dia-banda-sin_alimentos" className={`${pastilla} border-brand/45 bg-brand/10 text-brand`}>
                <UtensilsCrossed className="w-3.5 h-3.5" />
                {sinCrear.length === 1 ? '1 comida sin alimentos' : `${sinCrear.length} comidas sin alimentos`}
            </span>
        );
    }
    // EL INTRA Y EL POST QUE NO ESTÁN (MAC-04 y ALT-03, 14-10; dentro de la pastilla desde el
    // 15-10). Con las comidas creadas y el perientreno sin crear -- como deja el alta el primer
    // día --, al día le falta justo eso, y «Cuadrar el día» no lo arregla: en una comida vacía no
    // hay nada que repartir. Se dice qué falta crear, sin botón.
    if (periSinCrear.length > 0) {
        return (
            <span data-testid="dia-banda-sin_peri" className={`${pastilla} border-brand/45 bg-brand/10 text-brand`}>
                <UtensilsCrossed className="w-3.5 h-3.5" />
                {`Te falta crear ${periSinCrear.map((k) => `el ${nombreDe(k)}`).join(' y ')}`}
            </span>
        );
    }
    const lecturas = macros.map((x) => ({ ...x,
        color: leerMacro({ vista: 'dieta', hay: alDecima(x.valDia), objetivo: alDecima(x.tgtDia), redondo: true }).color }));
    const cortos = lecturas.filter((x) => x.color === 'falta').map((x) => enMinuscula(x.label));
    const pasados = lecturas.filter((x) => x.color === 'pasado' || x.color === 'sepasa').map((x) => enMinuscula(x.label));
    return (
        <span className="inline-flex items-center gap-2 flex-wrap">
            <span data-testid="dia-banda-sin_terminar" className={`${pastilla} ${cortos.length
                ? 'border-brand/45 bg-brand/10 text-brand' : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400'}`}>
                <TrendingDown className={`w-3.5 h-3.5 ${cortos.length ? '' : 'rotate-180'}`} />
                {cortos.length ? `Corto en ${yJunto(cortos)}` : pasados.length ? `Te pasas en ${yJunto(pasados)}` : 'Sin cuadrar'}
            </span>
            {onTerminarDia && (
                <button onClick={onTerminarDia} disabled={terminando} data-testid="boton-terminar-dia"
                    className="h-8 rounded-full font-bold text-xs px-3 bg-brand text-[#141414] hover:bg-brand/85 transition-colors disabled:opacity-50">
                    {terminando ? 'Cuadrando...' : 'Cuadrar el día'}
                </button>
            )}
        </span>
    );
};

const DayHeader = ({
    // su dieta no lleva perientreno (ver ConfigSection)
    sinPeri = false,
    // fecha
    currentDate, changeDate, onIrASemana, diaSinMarcar,
    // tipo de día y configuración
    tipoDia, handleSetTipoDia,
    numComidas, setNumComidas, momentoEntreno, setMomentoEntreno, opcionPeri, setOpcionPeri, singleMeal,
    configExpanded, setConfigExpanded,
    // macros
    dayMacros, dayTarget, servedPeriP, servedPeriH, servedPeriG = 0, totalPeriP, totalPeriH,
    // detalle
    summaryExpanded, setSummaryExpanded,
    mealOrder, mealInfo, calculateMealMacros, getMealStatus,
    // el estado del día, calculado fuera y en un solo sitio (ver NutritionPage)
    estadoDelDia, onTerminarDia, terminando,
    // La semana para la tira (`GET /diets/semana`) y el aviso de guardado, que en el
    // teléfono vive aquí desde que el título «Nutrición» se fue de la pantalla (16-10).
    semana = null, estadoGuardado = null,
}) => {
    // La parte de las COMIDAS: al día se le quita el peri, que lleva su propia cuenta
    // (el chip de abajo y las filas del detalle). De aquí sale el ESTADO del día.
    const mainP = dayMacros.P - servedPeriP;
    const mainH = dayMacros.H - servedPeriH;
    const mainG = dayMacros.G - servedPeriG;
    // Objetivo de las COMIDAS = total del día menos el peri que se cuenta aparte. No vale
    // usar P_entreno: en `sin_peri` (y en `solo_intra`) parte del presupuesto de perientreno
    // se reparte ENTRE LAS COMIDAS, así que los objetivos por comida suman más que los macros
    // de entreno. La cabecera pedía de menos justo ese peri repartido (30 P y 16 H en el caso
    // que lo destapó): con las cuatro comidas cuadradas el día decía "te pasas", y como nunca
    // quedaba UNA sola comida sin cuadrar tampoco aparecía "Volcar macros aquí".
    const tgtP = (dayTarget.P_total ?? 0) - (totalPeriP || 0);
    const tgtH = (dayTarget.H_total ?? 0) - (totalPeriH || 0);
    const tgtG = dayTarget.G_total ?? 0;   // el objetivo del peri no lleva grasa

    // LOS NÚMEROS GRANDES ENSEÑAN EL DÍA ENTERO, PERI INCLUIDO (doc 21-08, apartado 11):
    // arriba un solo objetivo y un solo servido, con el perientreno dentro, en los cuatro
    // modos de peri. El reparto interno no cambia: `val`/`tgt` siguen siendo los de las
    // comidas y de ahí sale el estado (el mismo criterio que getDayStatus); `valDia`/`tgtDia`
    // son solo lo que se pinta. El chip del perientreno sigue debajo con su propia cuenta.
    //
    // LA GRASA VA SIN EL PERI, Y ESE ERA EL DESCUADRE DEL PUNTO 178 (27-08): «el mismo día,
    // la grasa dice 40 aquí y 41 en Nutrición».
    // Aquí ponía `valDia: dayMacros.G`, que SÍ lleva la grasa del intra y del post, y lo
    // comparaba contra `tgtDia: tgtG`, que NO la lleva (en el método el objetivo del peri no
    // tiene grasa). Creado y objetivo medían cosas distintas en la misma columna. Y el Inicio
    // no tenía el fallo: allí lo montado sale de `servido_comidas`, que el servidor cuenta
    // sin el peri. Dos pantallas, dos números, y el bueno era el del Inicio.
    // La línea de arriba decía «en grasa día y comidas coinciden: el peri no lleva grasa»:
    // es una suposición, y en producción hay 27 días guardados que la desmienten (batidos
    // con 2 a 4 g de grasa en el intra o el post). `mainG` ya era el número bueno -- de él
    // sale el estado del día -- y solo se pintaba el otro.
    // LAS COMIDAS QUE TODAVÍA NO EXISTEN (12-09).
    //
    // El artefacto da una sola frase para el día a medias: «cada comida está dentro de su
    // margen, pero sumadas se quedan cortas». Es verdad en el caso que él dibuja -- las
    // cuatro comidas creadas y el total corto --, y es MENTIRA en el día que tiene una
    // comida sin crear: esa comida no está dentro de ningún margen, es que no está.
    //
    // Y se nota en el botón de al lado: «Terminar el día» reparte entre las comidas lo que
    // le falta al total, pero en una comida vacía no hay nada que repartir, y proponer
    // alimentos no lo hace ([[cuadrar-no-propone-alimentos]]). Así que el día se quedaba
    // corto, el cliente volvía a pulsar y volvía a pasar lo mismo, con la banda diciéndole
    // que todas sus comidas estaban bien.
    const sinCrear = (mealOrder || [])
        .filter((k) => k !== 'Intra' && k !== 'Post')
        .filter((k) => getMealStatus && getMealStatus(k) === 'empty');
    const periSinCrear = (mealOrder || [])
        .filter((k) => k === 'Intra' || k === 'Post')
        .filter((k) => getMealStatus && getMealStatus(k) === 'empty');

    // LO CREADO EN EL DÍA, CON LA CUENTA DE LAS DOS PANTALLAS (12-10, F10): `totalDelDia` de
    // lib/estadoDelDia sobre lo que suma cada comida, la misma función que usa Inicio. Es la
    // regla que ya se aplicaba aquí a mano (P y H con el peri, la grasa sin él).
    const creadoDelDia = totalDelDia(Object.fromEntries(
        (mealOrder || []).map((k) => [k, calculateMealMacros(k)])));
    const macros = [
        { key: 'P', label: 'Proteína', val: mainP, tgt: tgtP || 0, valDia: creadoDelDia.P, tgtDia: dayTarget.P_total ?? 0 },
        { key: 'H', label: 'Hidratos', val: mainH, tgt: tgtH || 0, valDia: creadoDelDia.H, tgtDia: dayTarget.H_total ?? 0 },
        { key: 'G', label: 'Grasa', val: mainG, tgt: tgtG || 0, valDia: creadoDelDia.G, tgtDia: tgtG || 0 },
    ];

    return (
        <section data-testid="day-summary" className="space-y-3">
            {/* EL DÍA, ARRIBA Y EN UNA LÍNEA (handoff de Nutrición, 16-10): el mes como antetítulo,
                «Jueves 16 · Hoy» y, a la derecha, Entreno/Descanso en pequeño (es un dato del
                día, no una sección). Tocar el título lleva a Mi semana, como antes la fecha. */}
            <div className="flex items-end justify-between gap-3" data-testid="dia-y-tipo">
                <button onClick={onIrASemana} data-testid="open-calendar-btn" title="Ver en Mi semana"
                    className="min-w-0 text-left">
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-brand">
                        Nutrición · {(() => {
                            const d = new Date(`${currentDate}T12:00:00`);
                            const mes = d.toLocaleDateString('es-ES', { month: 'long' });
                            return d.getFullYear() === ahoraDeLaApp().getFullYear() ? mes : `${mes} ${d.getFullYear()}`;
                        })()}
                    </span>
                    <span className="block font-heading text-[26px] font-bold leading-tight text-foreground first-letter:uppercase">
                        {(() => {
                            const d = new Date(`${currentDate}T12:00:00`);
                            return `${d.toLocaleDateString('es-ES', { weekday: 'long' })} ${d.getDate()} · ${cuandoEs(currentDate)}`;
                        })()}
                    </span>
                </button>
                <div className={`flex gap-0.5 rounded-full bg-muted p-[3px] flex-shrink-0 ${diaSinMarcar ? 'ring-2 ring-brand ring-offset-2 ring-offset-background' : ''}`}
                    data-testid="tipo-dia" role="radiogroup" aria-label="Tipo de día">
                    {[['entrenamiento', 'Entreno', Dumbbell], ['descanso', 'Descanso', Moon]].map(([id, texto, Icono]) => (
                        <button key={id} data-testid={`tipo-dia-${id}`} onClick={() => handleSetTipoDia(id)}
                            role="radio" aria-checked={tipoDia === id} aria-pressed={tipoDia === id}
                            className={`min-h-[36px] px-2.5 sm:px-3 rounded-full text-[13px] font-semibold transition-colors flex items-center gap-1.5 ${tipoDia === id ? 'bg-brand text-[#141414]' : 'text-muted-foreground hover:text-foreground'}`}>
                            <Icono className="w-3.5 h-3.5 hidden sm:block" /> {texto}
                        </button>
                    ))}
                </div>
            </div>
            {estadoGuardado && <div className="lg:hidden -mt-1 h-4">{estadoGuardado}</div>}

            {/* < > PARA CAMBIAR DE SEMANA (Francisco, 23-10). Deslizar ya saltaba siete días,
                pero no se ve y en el ordenador no existe. Las flechas hacen lo mismo. */}
            <div className="flex items-center gap-1">
                <button type="button" onClick={() => changeDate(-7)} data-testid="semana-anterior"
                    aria-label="Semana anterior"
                    className="shrink-0 w-8 h-10 grid place-items-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60">
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                    <TiraSemanal currentDate={currentDate} semana={semana} estadoDelDia={estadoDelDia} onIrA={changeDate} />
                </div>
                <button type="button" onClick={() => changeDate(7)} data-testid="semana-siguiente"
                    aria-label="Semana siguiente"
                    className="shrink-0 w-8 h-10 grid place-items-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60">
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            {/* «TOTAL DEL DÍA», COMPACTO (handoff, 16-10). Sustituye a «Lo que llevas creado» con
                sus tres cajas grandes: aquí basta saber cuánto llevas del día; el estado de color
                se queda para la comida, en la barra de abajo. Las barras van en blanco a
                propósito. La pastilla del día y «Cuadrar el día» siguen debajo. */}
            <div className="rounded-2xl border border-border bg-card p-3.5 space-y-2.5" data-testid="dia-resumen">
                <div className="flex items-baseline justify-between gap-3">
                    <h2 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">Total del día</h2>
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap font-data">
                        obj. {macros.map((x) => `${cifraDelDia(alDecima(x.tgtDia))}${x.key}`).join(' · ')}
                    </span>
                </div>
                <div className="grid grid-cols-3 gap-3" data-testid="dia-numeros">
                    {macros.map(({ key, label, valDia, tgtDia }) => {
                        const creado = alDecima(valDia);
                        const meta = alDecima(tgtDia);
                        const largo = meta > 0 ? Math.min(100, (creado / meta) * 100) : 0;
                        return (
                            <div key={key} className="min-w-0" data-testid={`dia-${key}`}>
                                <p className="text-xs text-muted-foreground">{label}</p>
                                <p className="whitespace-nowrap mt-0.5">
                                    <span className="font-heading text-xl font-bold text-foreground">{cifraDelDia(creado)}</span>
                                    <span className="text-sm text-muted-foreground"> / {cifraDelDia(meta)} g</span>
                                </p>
                                <div className="h-1 rounded-full bg-muted mt-1.5 overflow-hidden">
                                    <div className="h-full rounded-full bg-foreground transition-all duration-200" style={{ width: `${largo}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {estadoDelDia && (
                    <div className="pt-0.5">
                        {/* «Te falta crear el Post» (fusión del PR 3, 15-10): con el peri sin crear. */}
                        <EstadoDelDia estadoDelDia={estadoDelDia} sinCrear={sinCrear} macros={macros}
                            periSinCrear={periSinCrear} nombreDe={(k) => mealInfo?.[k]?.name || k}
                            onTerminarDia={onTerminarDia} terminando={terminando} />
                    </div>
                )}
            </div>

            {configExpanded && (
                <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3" data-testid="config-section">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                        <ConfigSection
                            inline
                            tipoDia={tipoDia}
                            momentoEntreno={momentoEntreno}
                            setMomentoEntreno={setMomentoEntreno}
                            opcionPeri={opcionPeri}
                            setOpcionPeri={setOpcionPeri}
                            numComidas={numComidas}
                            setNumComidas={setNumComidas}
                            singleMeal={singleMeal}
                            sinPeri={sinPeri}
                        />
                    </div>
                    {/* «EL LISTO ES MUY PEQUEÑO Y NO PARECE UN BOTÓN» (César, Trello 21-09) y
                        «está ahí pero no llama la atención» (Francisco, 21-09). Se queda donde
                        está, debajo de los selectores, pero como el botón principal que es: a
                        todo lo ancho y alto. Y al abrir el bloque la pantalla baja
                        hasta él, que en el teléfono caía justo detrás de la barra de abajo. */}
                    <button onClick={() => setConfigExpanded(false)} data-testid="cerrar-config"
                        ref={(el) => { if (el && !el.dataset.visto) { el.dataset.visto = '1'; setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50); } }}
                        className="mt-4 w-full h-12 rounded-xl font-bold text-base bg-brand text-white hover:bg-brand/85 transition-colors flex items-center justify-center gap-2">
                        Listo
                    </button>
                </div>
            )}

        </section>
    );
};

export default DayHeader;
